/**
 * The staged dataset, shown inside the composer above the text box.
 *
 * It reads entirely from the SCAN — the archive's own contents — and never from
 * the model. That is the whole trust argument for letting an assistant touch a
 * user's data: before anything is created, the user can see that the file was
 * understood ("1,248 images · 4 folders · folder-per-class · 412 MB") and that
 * the numbers match what they packed.
 */
import React from 'react';
import { useI18n } from '../../../i18n';
import { formatBytes } from '../utils/datasetScan';

const ComposerAttachTray = ({ attachment, onRemove, onReplace, disabled }) => {
  const { t } = useI18n();
  if (!attachment) return null;

  const {
    fileName, status, error, images, bytes, folders, layout, skipped,
    annotationFormat, classNames, classCount, unsupportedFormat, phase, count,
  } = attachment;
  const isReading = status === 'reading';
  const isBad = status === 'error';

  /**
   * What the spinner is actually waiting on.
   *
   * A spinner alone cannot distinguish "working" from "wedged", and reading a
   * 150 MB archive is long enough for a user to conclude the second. The phase
   * is named, and the file count is quoted the moment it is known, so the
   * number on screen changes while the work happens. `phase` is missing on an
   * older persisted attachment, so it falls back to the generic line.
   */
  const readingLabel = () => {
    if (phase === 'unpacking') return t('assistant.attach.phaseUnpacking');
    if (phase === 'scanning') {
      return t('assistant.attach.phaseScanning', { count: (count || 0).toLocaleString() });
    }
    if (phase === 'labels') {
      return t('assistant.attach.phaseLabels', { count: (count || 0).toLocaleString() });
    }
    return t('assistant.attach.reading', { name: '' });
  };

  return (
    <div
      className={`lf-attach${isBad ? ' lf-attach--bad' : ''}`}
      role={isBad ? 'alert' : 'status'}
    >
      <span className="lf-attach__icon" aria-hidden="true">
        <i className={`fa ${isReading ? 'fa-circle-notch fa-spin' : isBad ? 'fa-exclamation-triangle' : 'fa-file-archive'}`} />
      </span>

      <span className="lf-attach__body">
        <span className="lf-attach__name" title={fileName}>{fileName}</span>
        <span className="lf-attach__meta">
          {isReading && readingLabel()}
          {isBad && (error || t('assistant.attach.failed'))}
          {!isReading && !isBad && unsupportedFormat === 'voc' && (
            <span className="lf-attach__warn">{t('assistant.attach.unsupportedVoc')}</span>
          )}
          {!isReading && !isBad && (
            <>
              <span>{t('assistant.attach.summary', { images, size: formatBytes(bytes) })}</span>
              {folders > 0 && <span>{t('assistant.attach.folders', { count: folders })}</span>}
              <span className="lf-attach__tag">
                {annotationFormat === 'yolo' && t('assistant.attach.layoutYolo')}
                {annotationFormat === 'coco' && t('assistant.attach.layoutCoco')}
                {!annotationFormat && layout === 'folder_per_class' && t('assistant.attach.layoutFolders')}
                {!annotationFormat && layout !== 'folder_per_class' && t('assistant.attach.layoutFlat')}
              </span>
              {!!classCount && (
                <span title={(classNames || []).join(', ')}>
                  {t('assistant.attach.classPreview', {
                    names: (classNames || []).join(', ') + (classCount > (classNames || []).length ? '…' : ''),
                  })}
                </span>
              )}
              {skipped > 0 && <span>{t('assistant.attach.skipped', { count: skipped })}</span>}
            </>
          )}
        </span>
      </span>

      {isReading && <span className="lf-attach__bar" aria-hidden="true" />}

      {!isReading && (
        <button
          type="button"
          className="lf-attach__action"
          onClick={onReplace}
          disabled={disabled}
          title={t('assistant.attach.replace')}
        >
          {t('assistant.attach.replace')}
        </button>
      )}

      <button
        type="button"
        className="lf-attach__close"
        onClick={onRemove}
        disabled={disabled}
        title={t('assistant.attach.remove')}
        aria-label={t('assistant.attach.remove')}
      >
        <i className="fa fa-times" aria-hidden="true" />
      </button>
    </div>
  );
};

export default ComposerAttachTray;
